import { graphqlRequest } from "./client";
import {
  AI_FIELDS,
  CHECK_FIELDS,
  GATE_FIELDS,
  JOB_FIELDS,
  type AIFeedback,
  type CheckItem,
  type Job,
  type StepGate,
} from "./types";

export interface Bid {
  price: number;
  text: string;
  pct: number;
  counts: boolean;
  learning: boolean;
  checks: CheckItem[];
  blocks: string[];
  aiFeedback?: AIFeedback | null;
  submittedAt: string;
}

export interface JobTile {
  job: Job;
  bid?: Pick<Bid, "pct" | "counts"> | null;
}

export type JobBudget = "any" | "lt200" | "b200to500" | "b500to1000" | "gt1000";
export type JobPosted = "any" | "h24" | "d3" | "d7";
export type JobSort = "newest" | "budgetDesc" | "budgetAsc" | "fewest";
export type JobShow = "all" | "bid";

export interface JobFilter {
  search?: string;
  budget: JobBudget;
  posted: JobPosted;
  urgentOnly: boolean;
  sort: JobSort;
  show: JobShow;
  limit: number;
  pageNumber: number;
}

export interface SampleProposal {
  jobTitle: string;
  jobClient: string;
  lo: number;
  hi: number;
  price: number;
  text: string;
  checks: CheckItem[];
}

export interface BidScreen {
  job: Job;
  learning: boolean;
  learningIndex: number;
  sample?: SampleProposal | null;
  bid?: Bid | null;
  aiEnabled: boolean;
}

export interface PitchView {
  step: {
    readiness?: number | null;
    bidsCounted?: number | null;
  };
  bidsCounted: number;
  readiness?: number | null;
  gate: StepGate;
  done: boolean;
}

export const BIDS_REQUIRED = 10;
export const LEARNING_BIDS = 2;
export const BID_MIN_WORDS = 60;
export const BID_MAX_WORDS = 250;
export const COMPETITIVE_MIN = 6;

const BID_FIELDS = `price text pct counts learning checks { ${CHECK_FIELDS} } blocks aiFeedback { ${AI_FIELDS} } submittedAt`;
const PITCH_FIELDS = `step { readiness bidsCounted } bidsCounted readiness ${GATE_FIELDS} done`;

/** Step 3: Pitch. */
export async function fetchPitch(): Promise<PitchView> {
  return (await graphqlRequest<{ getPitch: PitchView }>(`{ getPitch { ${PITCH_FIELDS} } }`))
    .getPitch;
}

/** One page of the practice job board. */
export async function fetchJobBoard(
  filter: JobFilter,
): Promise<{ items: JobTile[]; total: number }> {
  return (
    await graphqlRequest<{ getJobBoard: { items: JobTile[]; total: number } }>(
      `query($filter: JobFilterInput!) { getJobBoard(filter: $filter) { total items { job { ${JOB_FIELDS} } bid { pct counts } } } }`,
      { variables: { filter } },
    )
  ).getJobBoard;
}

/** One job's brief, model proposal and the participant's bid. */
export async function fetchBidScreen(jobCode: string): Promise<BidScreen> {
  return (
    await graphqlRequest<{ getBidScreen: BidScreen }>(
      `query($code: String!) { getBidScreen(jobCode: $code) { learning learningIndex aiEnabled job { ${JOB_FIELDS} }
        sample { jobTitle jobClient lo hi price text checks { ${CHECK_FIELDS} } } bid { ${BID_FIELDS} } } }`,
      { variables: { code: jobCode } },
    )
  ).getBidScreen;
}

/** Submit or resubmit a bid. Scored by the server. */
export async function submitBid(jobCode: string, price: string, text: string): Promise<Bid> {
  return (
    await graphqlRequest<{ submitBid: Bid }>(
      `mutation($input: SubmitBidInput!) { submitBid(input: $input) { ${BID_FIELDS} } }`,
      { variables: { input: { jobCode, price, text } } },
    )
  ).submitBid;
}

/** Complete Step 3. */
export async function completePitch(): Promise<PitchView> {
  return (
    await graphqlRequest<{ completePitch: PitchView }>(
      `mutation { completePitch { ${PITCH_FIELDS} } }`,
    )
  ).completePitch;
}
